import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  ListItemAvatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Warning as WarningIcon, MergeType as MergeIcon, Send as SendIcon, Public as GlobalIcon } from '@mui/icons-material';
import { mergeRequestsApi } from '../../api';
import { PlayerAvatar } from './PlayerAvatar';
import type { BoardOfflinePlayer, BoardParticipant, MergeRequest, BoardPresenceInfo, GlobalMergeRequestResult } from '../../types';

export interface MergeOfflinePlayerDialogProps {
  open: boolean;
  offlinePlayers: BoardOfflinePlayer[];
  participants: BoardParticipant[];
  boardId: string;
  currentUserId?: string;
  onClose: () => void;
  onRequestCreated: (request: MergeRequest) => void;
  onGlobalRequestCreated?: (result: GlobalMergeRequestResult) => void;
  /** Initial offline player ID to pre-select (from merge suggestion notification) */
  initialOfflinePlayerId?: string;
  /** Initial target user ID to pre-select (from merge suggestion notification) */
  initialTargetUserId?: string;
}

export const MergeOfflinePlayerDialog: React.FC<MergeOfflinePlayerDialogProps> = ({
  open,
  offlinePlayers,
  participants,
  boardId,
  currentUserId,
  onClose,
  onRequestCreated,
  onGlobalRequestCreated,
  initialOfflinePlayerId,
  initialTargetUserId,
}) => {
  const [selectedOfflinePlayerId, setSelectedOfflinePlayerId] = useState<string>('');
  const [targetUserId, setTargetUserId] = useState<string>('');

  // Apply initial values when dialog opens with pre-selections, or reset if none provided
  useEffect(() => {
    if (open) {
      if (initialOfflinePlayerId) {
        setSelectedOfflinePlayerId(initialOfflinePlayerId);
      } else {
        setSelectedOfflinePlayerId('');
      }
      if (initialTargetUserId) {
        setTargetUserId(initialTargetUserId);
      } else {
        setTargetUserId('');
      }
    }
  }, [open, initialOfflinePlayerId, initialTargetUserId]);
  const [removeOfflinePlayer, setRemoveOfflinePlayer] = useState(true);
  const [mergeGlobally, setMergeGlobally] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boardPresence, setBoardPresence] = useState<BoardPresenceInfo[]>([]);
  const [loadingPresence, setLoadingPresence] = useState(false);

  // Filter out participants that don't have user info AND exclude current user
  const availableParticipants = useMemo(() =>
    participants.filter(p => p.user && p.userId && p.userId !== currentUserId),
    [participants, currentUserId]
  );

  const selectedOfflinePlayer = useMemo(() =>
    offlinePlayers.find(p => p.id === selectedOfflinePlayerId),
    [offlinePlayers, selectedOfflinePlayerId]
  );

  const selectedUser = useMemo(() =>
    availableParticipants.find(p => p.userId === targetUserId)?.user,
    [availableParticipants, targetUserId]
  );

  // Filter boards where target user is a member (these are the ones that will get merge requests)
  const validBoards = useMemo(() =>
    boardPresence.filter(b => b.targetUserIsMember === true),
    [boardPresence]
  );

  // Check if the offline player is on multiple boards where target is a member
  const isOnMultipleBoards = validBoards.length > 1;
  const totalSquaresAcrossBoards = useMemo(() =>
    validBoards.reduce((sum, b) => sum + b.squareCount, 0),
    [validBoards]
  );

  // Count boards where offline player has squares but target is not a member
  const boardsWithoutTarget = useMemo(() =>
    boardPresence.filter(b => b.targetUserIsMember === false),
    [boardPresence]
  );

  // Check if the current board is included in the valid boards list
  const currentBoardInMerge = useMemo(() =>
    validBoards.some(b => b.boardId === boardId),
    [validBoards, boardId]
  );

  // Check if current board is in the full presence list (has squares/ledger)
  const currentBoardHasPresence = useMemo(() =>
    boardPresence.some(b => b.boardId === boardId),
    [boardPresence, boardId]
  );

  // Get current board info from presence if available
  const currentBoardPresence = useMemo(() =>
    boardPresence.find(b => b.boardId === boardId),
    [boardPresence, boardId]
  );

  // Fetch board presence when an offline player and target user are selected
  const fetchBoardPresence = useCallback(async (playerId: string, targetId?: string) => {
    if (!playerId) {
      setBoardPresence([]);
      return;
    }

    setLoadingPresence(true);
    try {
      // Pass target user ID to get membership info for each board
      const presence = await mergeRequestsApi.getOfflinePlayerBoards(playerId, targetId);
      setBoardPresence(presence);
      // Auto-enable global merge if player is on multiple boards where target is a member
      const validCount = targetId
        ? presence.filter(b => b.targetUserIsMember === true).length
        : presence.length;
      if (validCount > 1) {
        setMergeGlobally(true);
      }
    } catch {
      // If fetching fails, just show single board mode
      setBoardPresence([]);
    } finally {
      setLoadingPresence(false);
    }
  }, []);

  // Re-fetch board presence when offline player or target user changes
  useEffect(() => {
    if (open && selectedOfflinePlayerId) {
      fetchBoardPresence(selectedOfflinePlayerId, targetUserId || undefined);
    }
  }, [open, selectedOfflinePlayerId, targetUserId, fetchBoardPresence]);

  const handleClose = () => {
    setSelectedOfflinePlayerId('');
    setTargetUserId('');
    setRemoveOfflinePlayer(true);
    setMergeGlobally(false);
    setBoardPresence([]);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOfflinePlayerId || !targetUserId) {
      setError('Please select both an offline player and a registered user');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (mergeGlobally && isOnMultipleBoards) {
        // Create global merge request
        const result = await mergeRequestsApi.createGlobal(selectedOfflinePlayerId, {
          targetUserId,
          removeOfflinePlayer,
        });
        onGlobalRequestCreated?.(result);
        // Also call onRequestCreated with the first request for backwards compatibility
        if (result.requests.length > 0) {
          onRequestCreated(result.requests[0]);
        }
      } else {
        // Create single-board merge request
        const request = await mergeRequestsApi.create(boardId, {
          offlinePlayerId: selectedOfflinePlayerId,
          targetUserId,
          removeOfflinePlayer,
        });
        onRequestCreated(request);
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create merge request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MergeIcon color="primary" />
          <Typography variant="h6" component="span">
            Request Player Merge
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This will send a merge request to the selected user. They must <strong>approve</strong> the request
            before any squares or payment records are transferred.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Offline Player Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="offline-player-label">Offline player to merge</InputLabel>
          <Select
            labelId="offline-player-label"
            value={selectedOfflinePlayerId}
            onChange={(e) => setSelectedOfflinePlayerId(e.target.value)}
            label="Offline player to merge"
            disabled={saving}
            renderValue={(selected) => {
              const player = offlinePlayers.find(p => p.id === selected);
              return player ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayerAvatar
                    displayName={player.displayName}
                    handle={player.handle}
                    iconName={player.iconName}
                    isOffline
                    size="small"
                    showBadge={false}
                  />
                  <Typography>{player.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({player.claimedSquares} squares)
                  </Typography>
                </Box>
              ) : '';
            }}
          >
            {offlinePlayers.length === 0 ? (
              <MenuItem disabled>
                <Typography color="text.secondary">
                  No offline players on this board
                </Typography>
              </MenuItem>
            ) : (
              offlinePlayers.map((player) => (
                <MenuItem key={player.id} value={player.id}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <PlayerAvatar
                      displayName={player.displayName}
                      handle={player.handle}
                      iconName={player.iconName}
                      isOffline
                      size="small"
                      showBadge={false}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={player.displayName}
                    secondary={`${player.claimedSquares} square${player.claimedSquares !== 1 ? 's' : ''} assigned`}
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Target User Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="target-user-label">Merge into registered user</InputLabel>
          <Select
            labelId="target-user-label"
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            label="Merge into registered user"
            disabled={saving}
            renderValue={(selected) => {
              const user = availableParticipants.find(p => p.userId === selected)?.user;
              return user ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayerAvatar
                    displayName={user.displayName}
                    handle={user.handle}
                    iconName={user.iconName}
                    size="small"
                    showBadge={false}
                  />
                  <Typography>{user.displayName}</Typography>
                </Box>
              ) : '';
            }}
          >
            {availableParticipants.length === 0 ? (
              <MenuItem disabled>
                <Typography color="text.secondary">
                  No other participants available
                </Typography>
              </MenuItem>
            ) : (
              availableParticipants.map((participant) => (
                <MenuItem key={participant.userId} value={participant.userId}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <PlayerAvatar
                      displayName={participant.user!.displayName}
                      handle={participant.user!.handle}
                      iconName={participant.user!.iconName}
                      size="small"
                      showBadge={false}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.user!.displayName}
                    secondary={participant.user!.handle ? `@${participant.user!.handle}` : participant.user!.email}
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={removeOfflinePlayer}
              onChange={(e) => setRemoveOfflinePlayer(e.target.checked)}
              disabled={saving}
            />
          }
          label="Remove offline player after merge"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          If unchecked, the offline player will remain on the board with 0 squares.
        </Typography>

        {/* Global Merge Option */}
        {selectedOfflinePlayerId && targetUserId && (
          <Box sx={{ mt: 2 }}>
            {loadingPresence ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Checking other boards...
                </Typography>
              </Box>
            ) : validBoards.length === 0 && boardPresence.length > 0 ? (
              // Offline player has squares on boards but target user is not on any of them
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Cannot merge on any board
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>{selectedUser?.displayName}</strong> is not a participant on any board where <strong>{selectedOfflinePlayer?.displayName}</strong> has squares.
                  The target user must join those boards before you can merge.
                </Typography>
              </Alert>
            ) : validBoards.length === 1 && !currentBoardInMerge ? (
              // Only one valid board but it's NOT the current board
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Merge will happen on a different board
                </Typography>
                <Typography variant="caption" component="div">
                  This merge will only affect <strong>{validBoards[0]?.boardName}</strong> ({validBoards[0]?.squareCount} squares).
                  {!currentBoardHasPresence ? (
                    <> <strong>{selectedOfflinePlayer?.displayName}</strong> has no squares on this board.</>
                  ) : (
                    <> <strong>{selectedUser?.displayName}</strong> is not a participant on this board.</>
                  )}
                </Typography>
              </Alert>
            ) : isOnMultipleBoards ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mergeGlobally}
                      onChange={(e) => setMergeGlobally(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GlobalIcon fontSize="small" color="primary" />
                      <span>Merge on all boards</span>
                      <Chip
                        size="small"
                        label={`${validBoards.length} boards`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Both players are on {validBoards.length} board{validBoards.length !== 1 ? 's' : ''} you manage.
                </Typography>

                {mergeGlobally && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Board</TableCell>
                          <TableCell align="right">Squares</TableCell>
                          <TableCell align="right">Ledger Entries</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validBoards.map((board) => (
                          <TableRow key={board.boardId}>
                            <TableCell>{board.boardName}</TableCell>
                            <TableCell align="right">{board.squareCount}</TableCell>
                            <TableCell align="right">{board.ledgerEntries}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                          <TableCell>Total</TableCell>
                          <TableCell align="right">{totalSquaresAcrossBoards}</TableCell>
                          <TableCell align="right">
                            {validBoards.reduce((sum, b) => sum + b.ledgerEntries, 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Warning when current board is NOT in the merge list */}
                {!currentBoardInMerge && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      This board is not included in the merge
                    </Typography>
                    <Typography variant="caption" component="div">
                      {!currentBoardHasPresence ? (
                        // Offline player has no squares/ledger on this board
                        <>
                          <strong>{selectedOfflinePlayer?.displayName}</strong> has no squares or payment records on this board.
                          {selectedOfflinePlayer && selectedOfflinePlayer.claimedSquares === 0 && (
                            <> You can safely delete this offline player from this board if needed.</>
                          )}
                        </>
                      ) : currentBoardPresence && !currentBoardPresence.targetUserIsMember ? (
                        // Target user is not a participant on this board
                        <>
                          <strong>{selectedUser?.displayName}</strong> is not a participant on this board.
                          To include this board in the merge, invite them to join first.
                        </>
                      ) : (
                        // Fallback (shouldn't happen)
                        <>This board does not meet the requirements for merging.</>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      After the global merge is approved, you will not see any changes on this board.
                    </Typography>
                  </Alert>
                )}

                {/* Info about boards where target user is not a member */}
                {boardsWithoutTarget.length > 0 && currentBoardInMerge && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      <strong>{selectedOfflinePlayer?.displayName}</strong> also has squares on {boardsWithoutTarget.length} other board{boardsWithoutTarget.length !== 1 ? 's' : ''} where <strong>{selectedUser?.displayName}</strong> is not a participant. Those boards are not included in this merge.
                    </Typography>
                  </Alert>
                )}
              </>
            ) : null}
          </Box>
        )}

        {selectedOfflinePlayer && selectedUser && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom>
              What will happen:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              1. <strong>{selectedUser.displayName}</strong> will receive a merge request notification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2. They can approve or deny the request
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3. If approved, {mergeGlobally && isOnMultipleBoards ? (
                <>{totalSquaresAcrossBoards} square{totalSquaresAcrossBoards !== 1 ? 's' : ''} across {validBoards.length} board{validBoards.length !== 1 ? 's' : ''}</>
              ) : (
                <>{selectedOfflinePlayer.claimedSquares} square{selectedOfflinePlayer.claimedSquares !== 1 ? 's' : ''}</>
              )} and all payment records will be transferred
            </Typography>
            {removeOfflinePlayer && (
              <Typography variant="body2" color="text.secondary">
                4. <strong>{selectedOfflinePlayer.displayName}</strong> will be removed from {mergeGlobally && isOnMultipleBoards ? `${validBoards.length} board${validBoards.length !== 1 ? 's' : ''}` : 'the board'}
              </Typography>
            )}
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
              <Typography variant="caption">
                The request will expire in 7 days if not acted upon.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            saving ||
            !selectedOfflinePlayerId ||
            !targetUserId ||
            // Disable when target user is not a participant on any board where offline player has squares
            (validBoards.length === 0 && boardPresence.length > 0)
          }
          startIcon={mergeGlobally && isOnMultipleBoards ? <GlobalIcon /> : <SendIcon />}
        >
          {saving
            ? 'Sending...'
            : mergeGlobally && isOnMultipleBoards
            ? `Send Global Merge Request (${validBoards.length} board${validBoards.length !== 1 ? 's' : ''})`
            : 'Send Merge Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeOfflinePlayerDialog;
